// Reading and writing a Ui::TextEditorComponent from ANOTHER Stimulus
// controller.
//
// The Work kit used to reach straight into the component's DOM —
// `[data-ui--text-editor-target='editor'].innerText`, `…='hidden'].value`,
// `editor.textContent = draft` — which worked only because the editor happened
// to be a contenteditable <div> with a mirror input beside it. On Lexxy the
// editing surface is inside a custom element that owns its own document model,
// the value lives on the element rather than in a sibling input, and writing to
// `textContent` would be thrown away on the next Lexical reconcile.
//
// So consumers go through the component's controller instead: `getText()`,
// `getHTML()`, `setValue()`, `clear()`, `focus()`, `isEmpty()`. That is the
// only supported way to talk to the editor, and it works on both engines.
//
// Every helper degrades to a no-op (or "") when there is no editor in the
// scope, or when its controller has not connected yet: a caller must never have
// to care which, and a missing composer is never worth an exception.

const IDENTIFIER = "ui--text-editor"
const SELECTOR = `[data-controller~='${IDENTIFIER}']`

// The component's ROOT element inside `scope` — the element carrying
// data-controller, not the editor target inside it.
export function textEditorElementIn(scope) {
  if (!scope) return null
  if (typeof scope.matches === "function" && scope.matches(SELECTOR)) return scope
  return scope.querySelector?.(SELECTOR) ?? null
}

export function textEditorIn(application, scope) {
  const element = textEditorElementIn(scope)
  if (!element || !application) return null
  return application.getControllerForElementAndIdentifier(element, IDENTIFIER)
}

export function editorText(application, scope) {
  return textEditorIn(application, scope)?.getText() ?? ""
}

export function editorHTML(application, scope) {
  return textEditorIn(application, scope)?.getHTML() ?? ""
}

export function editorIsEmpty(application, scope) {
  const controller = textEditorIn(application, scope)
  return controller ? controller.isEmpty() : true
}

export function setEditorHTML(application, scope, html) {
  textEditorIn(application, scope)?.setValue(html ?? "")
}

// Writes PLAIN TEXT into the editor. The Work kit's drafts and its failed-op
// restores are text, not markup (a comment body is never inserted as HTML
// anywhere in the kit), while the editor's value is HTML — so the text is
// escaped and wrapped in one paragraph rather than handed over raw. Passing it
// raw would let a draft reading `a < b` land as broken markup, or worse.
export function setEditorText(application, scope, text) {
  setEditorHTML(application, scope, textToParagraph(text))
}

export function clearEditor(application, scope) {
  textEditorIn(application, scope)?.clear()
}

export function focusEditor(application, scope) {
  textEditorIn(application, scope)?.focus()
}

function textToParagraph(text) {
  if (!text) return ""
  const div = document.createElement("div")
  div.textContent = text
  return `<p>${div.innerHTML}</p>`
}
